import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {useRequestsTable} from 'src/modules/vendor-admin/hooks/useRequestsTable';
import {
    selectIsLoadingRequests,
    selectRequests,
    selectRequestsTotalResults,
} from 'src/modules/vendor-admin/service/requests.slice';
import {getApprovalRequestChangelog} from 'src/openapi/admin/apis/RequestChangelogApi';
import {
    ApprovalRequestSearchCriteria,
    ApprovalRequestSearchCriteriaSortOrder,
} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import {RequestStatus} from 'src/openapi/admin/models/RequestStatus';
import {User} from 'src/openapi/admin/models/User';
import {useAppDispatch} from 'src/store/hooks';

import {RequestsLoadingState} from '../components/requests-loading-state';
import {RequestsTable} from '../components/requests-table';
import {ToolsRibbonComponent} from '../components/tools-ribbon-component';
import {RequestTopFilters} from '../filters/request-top-filters';

import type {ApprovalRequestChangelogItem} from 'src/openapi/admin/models/ApprovalRequestChangelogItem';

type Props = {
    criteria: ApprovalRequestSearchCriteria,
    onUpdateCriteria: (criteria: Partial<ApprovalRequestSearchCriteria>) => void,
    currentUser: User | null,
};

export const DeniedRequestsTabContent = (props: Props) => {
    const {criteria, onUpdateCriteria, currentUser} = props;
    const hasActiveFilter = !!(criteria.email || criteria.firstName || criteria.lastName || criteria.vendorId);
    const requests = useSelector(selectRequests);

    const isLoadingRequests = useSelector(selectIsLoadingRequests);
    const requestsTotalCount = useSelector(selectRequestsTotalResults);
    const {selectedIds, clearAll, selectPage} = useRequestsTable(requests);
    const pageSelectedCount = requests.reduce(
        (count, request) => count + (selectedIds.includes(request.requestId) ? 1 : 0),
        0,
    );
    const apiDispatch = useAppDispatch();
    const [changelog, setChangelog] = useState<Record<string, ApprovalRequestChangelogItem>>({});

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        const newCriteria: Partial<ApprovalRequestSearchCriteria> = {
            requestStatuses: [RequestStatus.DECLINED],
            page: 1,
            pageSize: 25,
            sortOrder: ApprovalRequestSearchCriteriaSortOrder.DESC,
        };
        if (currentUser?.vendor?.id) {
            newCriteria.vendorId = currentUser.vendor.id;
        }
        onUpdateCriteria(newCriteria);
    }, [onUpdateCriteria, currentUser]);

    useEffect(() => {
        if (isLoadingRequests || !requests.length) {
            return;
        }
        const requestIds = requests.map(r => r.requestId);
        apiDispatch(getApprovalRequestChangelog({
            approvalRequestChangelogRequest: {requestIds},
        })).then((result: unknown) => {
            const payload = (result as {payload?: {data?: {items?: ApprovalRequestChangelogItem[]}}}).payload;
            if (payload?.data?.items) {
                const map: Record<string, ApprovalRequestChangelogItem> = {};
                payload.data.items.forEach(item => {
                    map[item.requestId] = item;
                });
                setChangelog(map);
            }
        });
    }, [requests, isLoadingRequests, apiDispatch]);

    return (
        <>
            <RequestTopFilters
                criteria={criteria}
                onChange={onUpdateCriteria}
                currentUser={currentUser}
            />
            <ToolsRibbonComponent
                pageIds={requests.map(r => r.requestId)}
                selectedIds={selectedIds}
                pageSelectedCount={pageSelectedCount}
                onSelectPage={selectPage}
                onClear={clearAll}
            />
            {isLoadingRequests && !requests.length ? (
                <RequestsLoadingState/>
            ) : (
                <>
                    <RequestsTable
                        requests={requests}
                        criteria={criteria}
                        onUpdateCriteria={onUpdateCriteria}
                        enableStatusFilter={false}
                        hasActiveFilter={hasActiveFilter}
                        noResultsMessage="There are currently no denied requests"
                        showReasonColumn={true}
                        totalCount={requestsTotalCount}
                        changelog={changelog}
                        showDeniedByColumns={true}
                    />
                </>
            )}
        </>
    );
};
